<template>
  <v-card outlined style="position: relative; border-radius: 16px">
    <v-card-title class="d-flex flex-wrap justify-space-between align-center px-4 pt-4 pb-2">
      <span>Página de Agendamento</span>
      <div class="d-flex align-center">
        <span class="text-body-2 font-weight-medium mr-2">
          {{ form.status ? 'Ativo' : 'Inativo' }}
        </span>
        <v-switch v-model="form.status" hide-details class="mt-0 pt-0" />
      </div>
    </v-card-title>

    <v-card-text class="px-4 pt-2 pb-0">
      <p class="text-body-2 grey--text text--darken-1 mb-0">
        Crie o seu link, completando o campo abaixo com a identificação final do seu link.
      </p>

      <fieldset :disabled="!form.status" class="app-fieldset mt-4">
        <v-row>
          <v-col cols="12" md="6">
            <app-text-field prepend-inner-icon="mdi-link" :value="form.slug" @input="onSlugInput"
              label="Identificador do link" placeholder="meu-consultorio" :success="slugStatus === 'available'"
              :success-messages="slugSuccessMessages" :error-messages="slugErrorMessages" />

            <div v-if="slugStatus === 'checking'" class="checking-status">
              Verificando<span class="checking-dots">
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>

            <div v-if="form.slug && slugStatus !== 'checking'" class="link-preview-box mt-2">
              <span class="link-preview-url">
                {{ appClinicBaseUrl }}<strong>{{ form.slug }}</strong>
              </span>
              <v-btn v-if="clipboardSupported" rounded x-small outlined :color="copied ? 'success' : 'primary'"
                @click="handleCopyLink()" class="flex-shrink-0">
                <app-icon left small>{{ copied ? 'check' : 'content_copy' }}</app-icon>
                {{ copied ? 'Copiado!' : 'Copiar link' }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="6" md="3">
            <v-select :items="intervals" v-model="form.interval_booking" label="Intervalo de agendamento" dense
              hide-details />
          </v-col>

          <v-col cols="6" md="3">
            <v-select :items="sessionModalities" v-model="form.session_modality" label="Forma de atendimento" dense
              hide-details />
          </v-col>
        </v-row>
      </fieldset>
    </v-card-text>

    <v-card-actions class="px-4 pb-4 pt-2 d-flex justify-end">
      <app-btn @click="handleStore()" color="primary" :loading="isSaving" :disabled="!canSave">
        Salvar
      </app-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  data() {
    return {
      sessionModalities: [
        { text: "Presencial", value: "presential" },
        { text: "Online", value: "online" },
        { text: "Presencial e Online", value: "both" },
      ],
      intervals: [
        { text: '5 minutos', value: 5 },
        { text: '10 minutos', value: 10 },
        { text: '15 minutos', value: 15 },
      ],
      form: {},
      baseForm: {
        id: null,
        session_modality: null,
        interval_booking: null,
        slug: null,
        status: false,
      },
      slugStatus: null,
      slugDebounceTimer: null,
      isSaving: false,
      copied: false,
      copiedTimer: null,
    };
  },
  created() {
    this.shareLinkSupported = "share" in navigator;
  },

  computed: {
    appClinicBaseUrl() {
      return "https://agendar-psicoplanner.web.app/";
    },
    clipboardSupported() {
      return navigator.clipboard;
    },
    company() {
      return this.$store.state.auth.company;
    },
    user() {
      return this.$store.state.auth.user;
    },
    slugSuccessMessages() {
      return this.slugStatus === 'available' ? ['✓ Link disponível'] : [];
    },
    slugErrorMessages() {
      if (this.slugStatus === 'too_short') return ['Mínimo de 3 caracteres'];
      if (this.slugStatus === 'taken') return ['✗ Este link já está em uso'];
      return [];
    },
    canSave() {
      if (!this.form.status) return true;
      if (!this.form.slug) return false;
      if (
        this.slugStatus === 'checking' ||
        this.slugStatus === 'taken' ||
        this.slugStatus === 'too_short'
      ) return false;
      return true;
    },
  },

  async mounted() {
    await this.select();
  },

  beforeDestroy() {
    clearTimeout(this.slugDebounceTimer);
    clearTimeout(this.copiedTimer);
  },

  methods: {
    async select() {
      await this.$http.index("settings/app").then((response) => {
        if (response.app_clinic) {
          this.form = JSON.parse(JSON.stringify(response.app_clinic));
          if (this.form.slug) {
            this.slugStatus = 'available';
          }
        } else {
          this.form = { ...this.baseForm };
        }
        this.form.session_modality = this.user.session_modality;
        this.form.interval_booking = this.user.interval_booking;
      });
    },

    onSlugInput(value) {
      const sanitized = (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/--+/g, '-');

      this.form.slug = sanitized;

      clearTimeout(this.slugDebounceTimer);

      if (!sanitized) {
        this.slugStatus = null;
        return;
      }

      if (sanitized.length < 3) {
        this.slugStatus = 'too_short';
        return;
      }

      this.slugStatus = 'checking';
      this.slugDebounceTimer = setTimeout(() => {
        this.checkSlugAvailability(sanitized);
      }, 500);
    },

    async checkSlugAvailability(slug) {
      try {
        const response = await this.$http.index('settings/app/check-slug', {
          slug,
          exclude_id: this.form.id || undefined,
        });
        this.slugStatus = response.available ? 'available' : 'taken';
      } catch {
        this.slugStatus = null;
      }
    },

    handleStore() {
      if (!this.form.id) {
        this.store();
      } else {
        this.update();
      }
    },

    async store() {
      this.isSaving = true;
      try {
        await this.$http.store("settings/app", this.form)
          .then(() => this.processSaved());
      } finally {
        this.isSaving = false;
      }
    },

    async update() {
      this.isSaving = true;
      try {
        await this.$http.update("settings/app", this.form.id, this.form)
          .then(() => this.processSaved());
      } finally {
        this.isSaving = false;
      }
    },

    processSaved() {
      this.$auth.getLoginData();
      this.select();
      this.$loading.finish();
      this.$snackbar({
        message: "Configurações salvas com sucesso!",
        color: "success",
        timeout: 1000,
        showClose: false,
      });
    },

    handleCopyLink() {
      navigator.clipboard
        .writeText(`${this.appClinicBaseUrl}${this.form.slug}`)
        .then(() => {
          this.copied = true;
          clearTimeout(this.copiedTimer);
          this.copiedTimer = setTimeout(() => {
            this.copied = false;
          }, 2000);
        })
        .catch(() => { });
    },
  },
};
</script>

<style scoped>
.app-fieldset {
  border: none;
  padding: 0;
  transition: opacity 220ms ease;
}

.app-fieldset:disabled {
  opacity: 0.38;
  pointer-events: none;
}

/* Dots animados no "verificando..." */
.checking-status {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

@keyframes dot-blink {

  0%,
  80%,
  100% {
    opacity: 0;
  }

  40% {
    opacity: 1;
  }
}

.checking-dots span {
  display: inline-block;
  animation: dot-blink 1.1s infinite ease-in-out;
  opacity: 0;
}

.checking-dots span:nth-child(2) {
  animation-delay: 0.25s;
}

.checking-dots span:nth-child(3) {
  animation-delay: 0.50s;
}

/* Link preview box */
.link-preview-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background: #EDE9FE;
  border: 1px solid #DDD6FE;
  border-radius: 8px;
  flex-wrap: wrap;
}

.link-preview-url {
  font-size: 12.5px;
  color: #6D28D9;
  word-break: break-all;
  flex: 1;
  line-height: 1.4;
}

.link-preview-url strong {
  font-weight: 700;
  color: #7C3AED;
}
</style>
